/**
 * Add or edit one expense, in a panel beside the report.
 *
 * Mileage is its own form, as on the phone: you record a route, not a receipt.
 * Distance is entered one way and doubled for a round trip, and the amount is
 * worked out from the IRS rate in force on the trip's date — the rate is saved
 * on the entry, so a later rate change never rewrites a logged trip. A round
 * trip back on a later day saves as two one-way legs (see `mileageLegs`).
 *
 * Duplicate opens a new, unsaved form filled in from an entry (`copyOf`).
 * Save locks on the first click, so a double-click can't create it twice.
 */

import { useMemo, useRef, useState } from "react";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useToast } from "@/context/ToastContext";
import {
  pbError, receiptUrl, useExpenseCategories, useExpenseMutations, useExpenseReports, useExpenses, useMileageRates,
} from "@/hooks/useExpenses";
import { useClients } from "@/hooks/useTime";
import {
  dayOf, fmtDay, isInvoiced, isOnHold, mileageLegs, money, oneWayMiles, pastPlaces, sortCategories, todayIso,
} from "@/lib/expenses";
import type { Expense } from "@/lib/types";

import {
  RECEIPT_ACCEPT, ReceiptThumb, ReceiptViewer, Segmented, SidePanel, Toggle,
  acceptableReceipts, dangerBtn, ghostBtn, input, label, primaryBtn, useDropZone,
} from "./ui";

export function ExpenseEditor({ reportId, expense, copyOf, mileage, onDuplicate, onClose }: {
  reportId: string;
  /** Absent for a new entry. */
  expense?: Expense;
  /** A new entry filled in from this one (see `copyOfExpense`). */
  copyOf?: Expense;
  /** For a new entry: open the mileage form rather than the expense form. */
  mileage?: boolean;
  /** Offered while editing a saved entry. */
  onDuplicate?: (e: Expense) => void;
  onClose: () => void;
}) {
  const seed = expense ?? copyOf;
  const isMileage = seed ? !!seed.is_mileage : !!mileage;
  const props = { reportId, expense, copyOf, onDuplicate, onClose };
  return isMileage ? <MileageForm {...props} /> : <ExpenseForm {...props} />;
}

type FormProps = {
  reportId: string; expense?: Expense; copyOf?: Expense; onDuplicate?: (e: Expense) => void; onClose: () => void;
};

/**
 * One click, one record. `isPending` only disables Save after React
 * re-renders, so a quick double-click could get two saves in; this lock is
 * set synchronously. It stays set after a successful save (the panel closes)
 * and is released when a save fails, so you can retry.
 */
function useSaveLock() {
  const busy = useRef(false);
  const [locked, setLocked] = useState(false);
  return {
    locked,
    run: async (fn: () => Promise<boolean>) => {
      if (busy.current) return;
      busy.current = true; setLocked(true);
      let done = false;
      try { done = await fn(); }
      finally { if (!done) { busy.current = false; setLocked(false); } }
    },
  };
}

const copyNote = (text: string) => (
  <div style={{
    fontSize: 12, color: "var(--ink3)", padding: "8px 12px", borderRadius: 8,
    border: "1px dashed var(--line2)", marginBottom: 12,
  }}>{text}</div>
);

// ---- Expense ---------------------------------------------------------------

function ExpenseForm({ reportId, expense, copyOf, onDuplicate, onClose }: FormProps) {
  const { data: allCats = [] } = useExpenseCategories();
  const { saveExpense, deleteExpense } = useExpenseMutations();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const lock = useSaveLock();
  const seed = expense ?? copyOf;

  // The item's own category stays offered even if it has since been archived.
  const cats = sortCategories(allCats).filter((c) => !c.is_mileage && (!c.archived || c.id === seed?.category));
  const [date, setDate] = useState(dayOf(seed?.date) || todayIso());
  const [picked, setCategory] = useState(seed?.category ?? "");
  // Categories can arrive after the panel opens; until one is picked, the first is it.
  const category = picked || cats[0]?.id || "";
  const [amount, setAmount] = useState(seed?.amount != null ? String(seed.amount) : "");
  const [description, setDescription] = useState(seed?.description ?? "");
  const [notes, setNotes] = useState(seed?.notes ?? "");
  const [captured, setCaptured] = useState(!!expense?.receipt_captured);
  const [quicken, setQuicken] = useState(!!expense?.added_to_quicken);
  const [invoice, setInvoice] = useState(!!expense?.added_to_invoice);
  const billing = useBilling(reportId, seed);
  const [pending, setPending] = useState<File[]>([]);
  const [removed, setRemoved] = useState<string[]>([]);
  const [viewing, setViewing] = useState<{ url: string; name: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const existing = (expense?.receipts ?? []).filter((n) => !removed.includes(n));

  function intake(files: FileList | File[]) {
    const ok = acceptableReceipts(files, toast);
    if (!ok.length) return;
    setPending((p) => [...p, ...ok]);
    setCaptured(true); // it is captured now
  }
  const drop = useDropZone(intake);

  const save = () => lock.run(async () => {
    // Blank is allowed: log it now, price it later. It shows as "needs amount"
    // on the report until then.
    const n = amount.trim() === "" ? 0 : Number(amount);
    if (!Number.isFinite(n) || n < 0) { toast("That amount isn't a number"); return false; }
    const data: Partial<Expense> = {
      ...billing.fields, date, category, description: description.trim(), notes: notes.trim(),
      amount: Math.round(n * 100) / 100, is_mileage: false,
      receipt_captured: captured, added_to_quicken: quicken, added_to_invoice: invoice,
    };
    try {
      await saveExpense.mutateAsync({ id: expense?.id, data, add: pending, remove: removed });
      toast(billing.movedTo ? `Moved to ${billing.movedTo}` : expense ? "Expense saved" : "Expense added");
      onClose();
      return true;
    } catch (e) {
      toast("Couldn't save: " + pbError(e));
      return false;
    }
  });

  return (
    <SidePanel
      title={expense ? "Edit expense" : copyOf ? "Duplicate expense" : "Add expense"}
      onClose={onClose}
      footer={<>
        {expense && <button onClick={() => setConfirmDelete(true)} style={dangerBtn}>Delete</button>}
        {expense && onDuplicate && <button onClick={() => onDuplicate(expense)} style={ghostBtn}>Duplicate</button>}
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={ghostBtn}>Cancel</button>
        <button onClick={() => void save()} style={primaryBtn} disabled={lock.locked}>
          {lock.locked ? (pending.length ? "Uploading…" : "Saving…") : "Save expense"}
        </button>
      </>}
    >
      {copyOf && copyNote("A copy — check the date and amount. Receipts aren't copied.")}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <label style={label}>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} /></label>
        <label style={label}>
          Amount
          <input type="number" step="0.01" min={0} inputMode="decimal" value={amount === "0" ? "" : amount} onChange={(e) => setAmount(e.target.value)}
            style={input} placeholder="Add later" autoFocus={!expense} />
        </label>
      </div>
      <label style={label}>
        Category
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={input}>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}{c.archived ? " (archived)" : ""}</option>)}
        </select>
      </label>
      <label style={label}>
        Description
        <input value={description} onChange={(e) => setDescription(e.target.value)} style={input} placeholder="Hyatt Regency — 2 nights" />
      </label>

      <div style={label}>
        Receipts
        <div
          {...drop.props}
          style={{
            marginTop: 6, padding: 12, borderRadius: 10,
            border: `1.5px dashed ${drop.active ? "var(--accent)" : "var(--line2)"}`,
            background: drop.active ? "var(--accentTint2)" : "transparent",
          }}
        >
          {(existing.length > 0 || pending.length > 0) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 10 }}>
              {existing.map((n) => (
                <ReceiptThumb key={n} expense={expense!} name={n}
                  onOpen={() => setViewing({ url: receiptUrl(expense!, n), name: n })}
                  onRemove={() => setRemoved((r) => [...r, n])} />
              ))}
              {pending.map((f, i) => (
                <span key={i} style={{
                  display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, padding: "4px 10px",
                  borderRadius: 999, background: "var(--accentTint)", color: "var(--ink2)", alignSelf: "center",
                }}>
                  {f.name}
                  <button onClick={() => setPending((p) => p.filter((_, j) => j !== i))} aria-label={`Remove ${f.name}`}
                    style={{ border: "none", background: "none", cursor: "pointer", color: "var(--muted)", padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <button type="button" onClick={() => fileRef.current?.click()} style={ghostBtn}>Upload…</button>
            <span style={{ fontSize: 12, color: "var(--muted)" }}>or drop PDFs or images here</span>
          </div>
          {removed.length > 0 && (
            <div style={{ fontSize: 11.5, color: "var(--warn)", marginTop: 8 }}>
              {removed.length} receipt{removed.length === 1 ? "" : "s"} will be removed when you save.
            </div>
          )}
          <input ref={fileRef} type="file" multiple accept={RECEIPT_ACCEPT} style={{ display: "none" }}
            onChange={(e) => { if (e.target.files) intake(e.target.files); e.target.value = ""; }} />
        </div>
      </div>

      <div style={{ margin: "4px 0 12px" }}>
        <Toggle on={captured} onChange={setCaptured}>Receipt captured</Toggle>
        <Toggle on={quicken} onChange={setQuicken}>Added to Quicken</Toggle>
        <Toggle on={invoice} onChange={setInvoice}>Added to invoice</Toggle>
      </div>

      {billing.element}

      <label style={label}>Notes<textarea value={notes} onChange={(e) => setNotes(e.target.value)} style={{ ...input, minHeight: 60, resize: "vertical" }} /></label>

      {viewing && <ReceiptViewer url={viewing.url} name={viewing.name} onClose={() => setViewing(null)} />}
      {confirmDelete && expense && (
        <ConfirmDialog
          title="Delete this expense?"
          message="It is removed from the report on every device, with its receipts."
          onConfirm={async () => {
            try { await deleteExpense.mutateAsync(expense.id); toast("Expense deleted"); onClose(); }
            catch (e) { toast("Couldn't delete: " + pbError(e)); }
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </SidePanel>
  );
}

// ---- Mileage ---------------------------------------------------------------

function MileageForm({ reportId, expense, copyOf, onDuplicate, onClose }: FormProps) {
  const { data: cats = [] } = useExpenseCategories();
  const { data: rates = [] } = useMileageRates();
  const { data: all = [] } = useExpenses();
  const { saveExpense, deleteExpense } = useExpenseMutations();
  const toast = useToast();
  const lock = useSaveLock();
  const seed = expense ?? copyOf;
  // Once the outbound leg of a new trip is saved, a retry updates it rather
  // than logging it a second time.
  const savedId = useRef(expense?.id);

  const mileCat = sortCategories(cats).find((c) => c.is_mileage && !c.archived)
    ?? cats.find((c) => c.id === seed?.category && c.is_mileage);
  const places = useMemo(() => pastPlaces(all), [all]);

  const [date, setDate] = useState(dayOf(seed?.date) || todayIso());
  // Follows the trip date until set to another day.
  const [returnPicked, setReturnDate] = useState("");
  const returnDate = returnPicked || date;
  const [from, setFrom] = useState(seed?.from_location ?? "");
  const [to, setTo] = useState(seed?.to_location ?? "");
  const [roundTrip, setRoundTrip] = useState(!!seed?.round_trip);
  const [oneWay, setOneWay] = useState(seed ? String(oneWayMiles(seed)) : "");
  const [notes, setNotes] = useState(seed?.notes ?? "");
  const [quicken, setQuicken] = useState(!!expense?.added_to_quicken);
  const [invoice, setInvoice] = useState(!!expense?.added_to_invoice);
  const billing = useBilling(reportId, seed);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const legs = mileageLegs({ date, returnDate, from: from.trim(), to: to.trim(), oneWay: Number(oneWay) || 0, roundTrip, rates });
  const split = legs.length > 1;
  const backwards = split && returnDate < date;
  const total = legs.reduce((s, l) => s + l.amount, 0);

  const save = () => lock.run(async () => {
    if (!mileCat) return false;
    if (backwards) { toast("The return date is before the trip date"); return false; }
    // A trip can be logged before you know the distance; it reads "needs
    // amount" on the report until the miles go in. The route also goes in
    // `description` (in each leg), so the CSVs and the package show where you
    // drove without knowing about the mileage fields.
    const common: Partial<Expense> = {
      ...billing.fields, category: mileCat.id, is_mileage: true,
      notes: notes.trim(), added_to_quicken: quicken, added_to_invoice: invoice,
    };
    try {
      const out = await saveExpense.mutateAsync({ id: savedId.current, data: { ...common, ...legs[0] } });
      savedId.current = out.id;
      if (split) await saveExpense.mutateAsync({ data: { ...common, ...legs[1] } });
      toast(billing.movedTo ? `Moved to ${billing.movedTo}` : split ? "Both trips logged" : expense ? "Mileage saved" : "Mileage logged");
      onClose();
      return true;
    } catch (e) {
      toast("Couldn't save: " + pbError(e));
      return false;
    }
  });

  if (!mileCat) {
    return (
      <SidePanel title="Log mileage" onClose={onClose}>
        <p style={{ fontSize: 13, color: "var(--ink3)" }}>
          No mileage category yet. Mark one category as a mileage category under
          Settings → Expense categories first.
        </p>
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={expense ? "Edit mileage" : copyOf ? "Duplicate mileage" : "Log mileage"}
      onClose={onClose}
      footer={<>
        {expense && <button onClick={() => setConfirmDelete(true)} style={dangerBtn}>Delete</button>}
        {expense && onDuplicate && <button onClick={() => onDuplicate(expense)} style={ghostBtn}>Duplicate</button>}
        <span style={{ flex: 1 }} />
        <button onClick={onClose} style={ghostBtn}>Cancel</button>
        <button onClick={() => void save()} style={primaryBtn} disabled={lock.locked}>{lock.locked ? "Saving…" : "Save mileage"}</button>
      </>}
    >
      {copyOf && copyNote("A copy — check the date before saving.")}
      <div style={roundTrip ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 } : undefined}>
        <label style={label}>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} /></label>
        {roundTrip && (
          <label style={label}>
            Return date
            <input type="date" value={returnDate} min={date} onChange={(e) => setReturnDate(e.target.value === date ? "" : e.target.value)} style={input} />
          </label>
        )}
      </div>
      {roundTrip && (
        <div style={{ fontSize: 11.5, color: backwards ? "var(--warn)" : "var(--muted)", margin: "-6px 0 12px" }}>
          {backwards ? "The return date is before the trip date."
            : "Coming back another day? Change the return date and it saves as two one-way trips, one on each date."}
        </div>
      )}
      <datalist id="mileage-places">{places.map((p) => <option key={p} value={p} />)}</datalist>
      <label style={label}>From<input list="mileage-places" value={from} onChange={(e) => setFrom(e.target.value)} style={input} placeholder="Home" autoFocus={!expense} /></label>
      <label style={label}>To<input list="mileage-places" value={to} onChange={(e) => setTo(e.target.value)} style={input} placeholder="Acme HQ" /></label>
      <div style={label}>
        Trip
        <div><Segmented value={roundTrip ? "round" : "one"} options={[["one", "One way"], ["round", "Round trip"]]} onChange={(v) => setRoundTrip(v === "round")} /></div>
      </div>
      <label style={label}>
        Distance one way (miles)
        <input type="number" step="0.1" inputMode="decimal" value={oneWay} onChange={(e) => setOneWay(e.target.value)} style={input} placeholder="Miles" />
      </label>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, fontSize: 12.5, padding: "10px 12px", borderRadius: 8,
        background: "var(--accentTint2)", border: "1px solid var(--line)", marginBottom: 12,
      }}>
        <span style={{ flex: 1, color: "var(--ink3)" }}>
          {split
            ? legs.map((l, i) => <span key={i} style={{ display: "block" }}>{fmtDay(l.date)}: {l.miles} mi × {money(l.mileage_rate)}/mi{i ? " (return)" : ""}</span>)
            : <>{Number(oneWay) || 0} mi{roundTrip ? " × 2 (round trip)" : ""} · {legs[0].miles} mi × {money(legs[0].mileage_rate)}/mi</>}
          {!rates.length && <span style={{ color: "var(--warn)" }}> · no mileage rate set</span>}
        </span>
        <strong className="mono">{money(total)}</strong>
      </div>
      <label style={label}>Purpose<input value={notes} onChange={(e) => setNotes(e.target.value)} style={input} placeholder="Optional — what the trip was for" /></label>
      <div>
        <Toggle on={quicken} onChange={setQuicken}>Added to Quicken</Toggle>
        <Toggle on={invoice} onChange={setInvoice}>Added to invoice</Toggle>
      </div>

      {billing.element}

      {confirmDelete && expense && (
        <ConfirmDialog
          title="Delete this mileage entry?"
          message="It is removed from the report on every device."
          onConfirm={async () => {
            try { await deleteExpense.mutateAsync(expense.id); toast("Mileage deleted"); onClose(); }
            catch (e) { toast("Couldn't delete: " + pbError(e)); }
          }}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </SidePanel>
  );
}

// ---- Which report, and whether to bill it yet -------------------------------

/**
 * The two ways to keep an expense off a report's bill: move it to the report
 * it belongs with (a ticket bought now for next month's trip), or keep it here
 * on hold until it is time to bill it. Hold is the existing `reimbursable`
 * field, which both apps already leave out of totals and the package.
 */
function useBilling(reportId: string, expense?: Expense) {
  const { data: reports = [] } = useExpenseReports();
  const { data: clients = [] } = useClients();
  const [report, setReport] = useState(expense?.report ?? reportId);
  const [hold, setHold] = useState(expense ? isOnHold(expense) : false);

  // Open reports, plus wherever this item is now even if that one is invoiced.
  const choices = reports.filter((r) => !isInvoiced(r.status) || r.id === report || r.id === reportId);
  const clientName = (id: string) => clients.find((c) => c.id === id)?.name ?? "";
  const target = reports.find((r) => r.id === report);
  const moved = report !== (expense?.report ?? reportId);

  const element = (
    <div style={{ padding: "10px 12px", borderRadius: 10, border: "1px solid var(--line)", background: "var(--tint)", marginBottom: 12 }}>
      <label style={{ ...label, marginBottom: 8 }}>
        Report
        <select value={report} onChange={(e) => setReport(e.target.value)} style={input}>
          {choices.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}{clientName(r.client) ? ` — ${clientName(r.client)}` : ""}{isInvoiced(r.status) ? " (invoiced)" : ""}
            </option>
          ))}
        </select>
        {moved && target && (
          <span style={{ display: "block", fontSize: 11.5, color: "var(--accent)", marginTop: 5 }}>
            Saving moves it to {target.name}, with its receipts.
          </span>
        )}
      </label>
      <Toggle on={hold} onChange={setHold}>On hold — don't bill yet</Toggle>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 2 }}>
        {hold
          ? "Stays listed on the report, but is left out of its total, the package and the reimbursement CSV until you turn this off."
          : "Counts toward this report's total and appears on its package."}
      </div>
    </div>
  );

  return {
    element,
    fields: { report, reimbursable: !hold } as Partial<Expense>,
    movedTo: moved ? target?.name : undefined,
  };
}
